import { Home, UtensilsCrossed, Users, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { usePrimaryAction } from "@/hooks/usePrimaryAction";

interface BottomNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
}

const tabs = [
  { id: "feed", icon: Home, label: "Home" },
  { id: "menus", icon: UtensilsCrossed, label: "Restaurants" },
  { id: "friends", icon: Users, label: "Friends" },
  { id: "myeatzy", icon: LayoutGrid, label: "My Eatzy" },
];

export default function BottomNavigation({
  activeTab,
  onTabChange,
  className,
}: BottomNavigationProps) {
  const primaryAction = usePrimaryAction();

  const handleTabClick = (tabId: string) => {
    logger.log(`Navigating to ${tabId}`);
    onTabChange(tabId);
  };

  const PrimaryIcon = primaryAction.icon;

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-card border-t border-border safe-area-padding-bottom z-50",
        className
      )}
    >
      <div className="flex items-center h-16 px-1">
        {tabs.slice(0, 2).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                "flex-1 min-w-0 flex flex-col items-center justify-center gap-1 px-1 py-2 h-auto min-h-12",
                isActive ? "text-eatzy-teal" : "text-muted-foreground"
              )}
              data-testid={`nav-${tab.id}`}
            >
              <span className="relative">
                <Icon
                  className={cn("w-6 h-6 shrink-0", isActive && "fill-current")}
                />
              </span>
              <span className="text-xs font-medium truncate max-w-full">
                {tab.label}
              </span>
            </Button>
          );
        })}

        <Button
          variant="default"
          onClick={() => {
            logger.log(`Primary action: ${primaryAction.kind}`);
            primaryAction.onClick();
          }}
          className="shrink-0 rounded-full px-4 h-10 bg-primary flex items-center gap-1.5 mx-1"
          data-testid={primaryAction.testId}
          data-action-kind={primaryAction.kind}
        >
          <PrimaryIcon
            className={cn(
              "w-4 h-4",
              (primaryAction.kind === "review" ||
                primaryAction.kind === "review-order") &&
                "fill-current"
            )}
          />
          <span className="text-sm font-semibold whitespace-nowrap">
            {primaryAction.label}
          </span>
        </Button>

        {tabs.slice(2).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                "flex-1 min-w-0 flex flex-col items-center justify-center gap-1 px-1 py-2 h-auto min-h-12",
                isActive ? "text-eatzy-teal" : "text-muted-foreground"
              )}
              data-testid={`nav-${tab.id}`}
            >
              <span className="relative">
                <Icon
                  className={cn("w-6 h-6 shrink-0", isActive && "fill-current")}
                />
              </span>
              <span className="text-xs font-medium truncate max-w-full">
                {tab.label}
              </span>
            </Button>
          );
        })}
      </div>
    </nav>
  );
}
